import React, {useCallback, useEffect, useState} from 'react';
import DOMPurify from 'dompurify';
import ModalContainer from '../../../../components/ModalContainer';
import FetchService from '../../../../services/api/FetchService';
import useQProfile from '../../../../queries/useQProfile';
import {IReasoningResource} from '../../../../resources/IReasoning.resource';
import {toast} from 'react-toastify';
import {ErrorToastSettings} from '../../../../services/toast/toastSettings';
import styles from './styles.module.scss';

interface IProps {
    companyId: number;
    phonecallUuid: string;
    checklistId: number;
    itemUuid: string;
    questionLabel: string;
    handleClose: () => void;
}

const ReasoningDialog: React.FC<IProps> = ({companyId, phonecallUuid, checklistId, itemUuid, questionLabel, handleClose}) => {
    const {profile} = useQProfile();
    const [reasonings, setReasonings] = useState<IReasoningResource[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [addText, setAddText] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fetchReasonings = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await FetchService.getItemReasoning(companyId, phonecallUuid, checklistId, itemUuid);
            setReasonings(res.data);
        } catch {
            toast.error('Не вдалося завантажити обґрунтування', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, phonecallUuid, checklistId, itemUuid]);

    useEffect(() => {
        fetchReasonings();
    }, [fetchReasonings]);

    const handleAdd = async () => {
        if (!addText.trim() || isSaving) return;
        try {
            setIsSaving(true);
            await FetchService.postItemReasoning(companyId, phonecallUuid, checklistId, itemUuid, {reasoning: addText.trim()});
            setAddText('');
            setIsAdding(false);
            await fetchReasonings();
        } catch {
            toast.error('Не вдалося додати обґрунтування', ErrorToastSettings);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditSave = async (id: number) => {
        if (!editText.trim() || isSaving) return;
        try {
            setIsSaving(true);
            await FetchService.putItemReasoning(companyId, phonecallUuid, checklistId, itemUuid, id, {reasoning: editText.trim()});
            setEditId(null);
            setEditText('');
            await fetchReasonings();
        } catch {
            toast.error('Не вдалося оновити обґрунтування', ErrorToastSettings);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (isSaving) return;
        try {
            setIsSaving(true);
            await FetchService.deleteItemReasoning(companyId, phonecallUuid, checklistId, itemUuid, id);
            setReasonings((prev) => prev.filter((r) => r.id !== id));
        } catch {
            toast.error('Не вдалося видалити обґрунтування', ErrorToastSettings);
        } finally {
            setIsSaving(false);
        }
    };

    const handleStartEdit = (r: IReasoningResource) => {
        setEditId(r.id);
        setEditText(r.reasoning);
        setIsAdding(false);
    };

    const handleCancelEdit = () => {
        setEditId(null);
        setEditText('');
    };

    const handleStartAdd = () => {
        setIsAdding(true);
        setEditId(null);
        setEditText('');
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setAddText('');
    };

    return (
        <ModalContainer handleClose={handleClose}>
            <div className={styles.dialog}>
                <div className={styles.dialog__header}>
                    <span className={styles.dialog__title}>{questionLabel}</span>
                    <button type="button" className={styles.dialog__close} onClick={handleClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M12 4L4 12M4 4L12 12" stroke="black" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className={styles.dialog__body}>
                    {isLoading ? (
                        <div className={styles.dialog__loader}>Завантаження...</div>
                    ) : (
                        <table className={styles.table} cellSpacing={0} cellPadding={0}>
                            <thead>
                                <tr>
                                    <th className={styles.table__th}>Дата</th>
                                    <th className={styles.table__th}>Автор</th>
                                    <th className={styles.table__th}>Обґрунтування</th>
                                    <th className={styles.table__th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {reasonings.map((r) => {
                                    const isOwn = profile?.id === r.user.id;
                                    const isEditing = editId === r.id;
                                    const date = new Date(r.updated);
                                    const dateStr = isNaN(date.getTime()) ? r.updated : date.toLocaleString('uk-UA');
                                    return (
                                        <tr key={r.id} className={styles.table__row}>
                                            <td className={styles.table__td}>{dateStr}</td>
                                            <td className={styles.table__td}>{r.user.firstName} {r.user.lastName}</td>
                                            <td className={styles.table__td}>
                                                {isEditing ? (
                                                    <textarea
                                                        className={styles.table__textarea}
                                                        value={editText}
                                                        onChange={(e) => setEditText(e.currentTarget.value)}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(r.reasoning)}} />
                                                )}
                                            </td>
                                            <td className={styles.table__tdActions}>
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={styles.table__actionBtn}
                                                            disabled={isSaving}
                                                            onClick={() => handleEditSave(r.id)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                <path d="M2 7L5.5 10.5L12 3.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={styles.table__actionBtn}
                                                            onClick={handleCancelEdit}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="black" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round"/>
                                                            </svg>
                                                        </button>
                                                    </>
                                                ) : isOwn ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={styles.table__actionBtn}
                                                            onClick={() => handleStartEdit(r)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                <path d="M9.5 2.5L11.5 4.5L4.5 11.5H2.5V9.5L9.5 2.5Z" stroke="black" strokeOpacity="0.5" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={styles.table__actionBtn}
                                                            onClick={() => handleDelete(r.id)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                <path d="M2 4H12M5 4V2.5H9V4M5.5 6.5V10.5M8.5 6.5V10.5M3 4L3.5 11.5H10.5L11 4" stroke="black" strokeOpacity="0.5" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        </button>
                                                    </>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {isAdding && (
                                    <tr className={styles.table__row}>
                                        <td className={styles.table__td} colSpan={2}></td>
                                        <td className={styles.table__td}>
                                            <textarea
                                                className={styles.table__textarea}
                                                value={addText}
                                                onChange={(e) => setAddText(e.currentTarget.value)}
                                                autoFocus
                                                placeholder="Введіть обґрунтування..."
                                            />
                                        </td>
                                        <td className={styles.table__tdActions}>
                                            <button
                                                type="button"
                                                className={styles.table__actionBtn}
                                                disabled={isSaving || !addText.trim()}
                                                onClick={handleAdd}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M2 7L5.5 10.5L12 3.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.table__actionBtn}
                                                onClick={handleCancelAdd}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="black" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className={styles.dialog__footer}>
                    {!isAdding && (
                        <button
                            type="button"
                            className={styles.dialog__addBtn}
                            onClick={handleStartAdd}
                        >
                            + Додати
                        </button>
                    )}
                </div>
            </div>
        </ModalContainer>
    );
};

export default ReasoningDialog;
